import clsx from "clsx"
import React, { useState } from "react"
import { Box, Dialog, Link, makeStyles, Typography } from "@material-ui/core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBell } from "@fortawesome/pro-light-svg-icons/faBell"
import { faCheckCircle } from "@fortawesome/pro-light-svg-icons/faCheckCircle"
import { faExclamationCircle } from "@fortawesome/pro-light-svg-icons/faExclamationCircle"
import { alpha } from "@material-ui/core/styles"
import { dangerColor, successColor } from "shared/theme"
import { Screen } from "shared/Components/Skeleton/Screen"
import { useAppNavigation } from "shared/hooks/useAppNavigation"
import { StandardLoadingButton } from "shared/Components/Loading/LoadingButton"
import { LocalizedStrict } from "shared/Modules/Localization/Components/Localized"
import { PushTypeToggles } from "shared/Modules/Notification/Components/PushTypeToggles"
import { useOpenNotificationSettings } from "shared/Modules/Notification/useOpenNotificationSettings"
import { DEFAULT_PUSH_NOTIFICATION_TYPES, PushNotificationTypes, usePushPermission } from "shared/Modules/Notification/usePushPermission"
import { ACCEPT_PUSH_FOR_NEWS_SETTING, ACCEPT_PUSH_FOR_TX_SETTING } from "shared/Modules/User/userSelectors"

const useStyles = makeStyles((theme) => ({
    status: {
        display: "flex",
        gap: theme.spacing(1.5),
        alignItems: "flex-start",
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(3),
    },
    statusIcon: {
        marginTop: theme.spacing(0.25),
    },
    statusAllowedIcon: {
        color: successColor,
    },
    statusDeniedBox: {
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1.5),
        backgroundColor: alpha(dangerColor, 0.08),
    },
    statusDeniedIcon: {
        color: dangerColor,
    },
    statusDeniedTitle: {
        fontWeight: "bold",
        color: dangerColor,
    },
    statusText: {
        flex: 1,
    },
    allowButton: {
        marginTop: theme.spacing(1.5),
    },
    settingsLink: {
        display: "inline-block",
        marginTop: theme.spacing(1),
        fontWeight: "bold",
    },
}))

type Props = Readonly<{
    onBack: () => void
}>

/**
 * Me > Settings > Push notifications: shows whether push is allowed on this phone and lets the user
 * change which kinds of notifications to receive. Reachable from the settings page and, on iOS, from
 * the app's notification settings in iOS settings (route /me/settings/push-notifications).
 */
export function PushNotificationSettings({ onBack }: Props) {
    const classes = useStyles()
    const { settings, osPermission, canRequestPermission, requesting, saving, requestPermission, saveType } = usePushPermission()
    const [savingType, setSavingType] = useState<keyof PushNotificationTypes>()
    const openNotificationSettings = useOpenNotificationSettings()

    const types: PushNotificationTypes = {
        forTx: settings.forTx ?? DEFAULT_PUSH_NOTIFICATION_TYPES.forTx,
        forNews: settings.forNews ?? DEFAULT_PUSH_NOTIFICATION_TYPES.forNews,
    }

    function handleTypeChange(type: keyof PushNotificationTypes, checked: boolean) {
        setSavingType(type)
        saveType(type === "forTx" ? ACCEPT_PUSH_FOR_TX_SETTING : ACCEPT_PUSH_FOR_NEWS_SETTING, checked)
            .finally(() => setSavingType(undefined))
    }

    function handleAllow() {
        // Android shows the dialog again; iOS answers with the stored decision right away (harmless)
        requestPermission(types)
    }

    function renderStatus() {
        if (!canRequestPermission) {
            return (
                <Box className={classes.status}>
                    <LocalizedStrict id="push-settings-status-unavailable">
                        <Typography variant="body2" color="textSecondary" className={classes.statusText}>
                            Pushbeskeder er kun tilgængelige i appen. Dine valg herunder gemmes og bruges i appen.
                        </Typography>
                    </LocalizedStrict>
                </Box>
            )
        }

        if (osPermission) {
            return (
                <Box className={classes.status}>
                    <FontAwesomeIcon icon={faCheckCircle} className={clsx(classes.statusIcon, classes.statusAllowedIcon)} />
                    <Box className={classes.statusText}>
                        <LocalizedStrict id="push-settings-status-allowed">
                            <Typography variant="body2">
                                Pushbeskeder er tilladt på denne telefon
                            </Typography>
                        </LocalizedStrict>
                        {openNotificationSettings && (
                            <LocalizedStrict id="push-settings-turn-off-link">
                                <Link component="button" variant="body2" underline="none" className={classes.settingsLink} onClick={openNotificationSettings}>
                                    Slå helt fra i telefonens indstillinger
                                </Link>
                            </LocalizedStrict>
                        )}
                    </Box>
                </Box>
            )
        }

        return (
            <Box className={clsx(classes.status, classes.statusDeniedBox)}>
                <FontAwesomeIcon icon={faExclamationCircle} className={clsx(classes.statusIcon, classes.statusDeniedIcon)} />
                <Box className={classes.statusText}>
                    <LocalizedStrict id="push-settings-status-denied-title">
                        <Typography variant="body2" className={classes.statusDeniedTitle}>
                            Slået fra på denne telefon
                        </Typography>
                    </LocalizedStrict>
                    <LocalizedStrict id="push-settings-status-denied-text">
                        <Typography variant="body2" color="textSecondary">
                            Uanset valgene herunder sender vi ingen beskeder, før du tillader dem på denne telefon.
                        </Typography>
                    </LocalizedStrict>
                    {openNotificationSettings ? (
                        // Once denied, iOS never shows the permission dialog again: the user must go to OS settings
                        <LocalizedStrict id="push-settings-open-settings-link">
                            <Link component="button" variant="body2" underline="none" className={classes.settingsLink} onClick={openNotificationSettings}>
                                Åbn indstillinger
                            </Link>
                        </LocalizedStrict>
                    ) : (
                        // No way to open the OS settings from here: at least try the OS dialog
                        <LocalizedStrict id="push-settings-allow-button" attrs={{ loadingLabel: true }}>
                            <StandardLoadingButton
                                size="small"
                                variant="contained"
                                color="black"
                                loading={requesting || saving}
                                loadingLabel="Gemmer..."
                                onClick={handleAllow}
                                className={classes.allowButton}
                            >
                                Tillad pushbeskeder
                            </StandardLoadingButton>
                        </LocalizedStrict>
                    )}
                </Box>
            </Box>
        )
    }

    return (
        <LocalizedStrict id="push-settings-title" attrs={{ primaryTopBarTitle: true }}>
            <Screen
                name="PushNotificationSettings"
                backButtonIcon="back"
                primaryTopBarTitle="Pushbeskeder"
                onBackButtonPress={onBack}
                showHeaderSection
                headerIcon={faBell}
            >
                {renderStatus()}
                <PushTypeToggles
                    value={types}
                    saving={savingType}
                    disabled={requesting || savingType !== undefined}
                    onChange={handleTypeChange}
                />
            </Screen>
        </LocalizedStrict>
    )
}

type DialogProps = Readonly<{
    isOpen: boolean
    onClose: () => void
}>

/** The same screen as a full-screen dialog, opened from the settings page */
export function PushNotificationSettingsDialog({ isOpen, onClose }: DialogProps) {
    return (
        <Dialog fullScreen open={isOpen}>
            <PushNotificationSettings onBack={onClose} />
        </Dialog>
    )
}

/** Route screen (/me/settings/push-notifications), e.g. deep linked from iOS notification settings */
export default function PushNotificationSettingsScreen() {
    const { goToPrevious } = useAppNavigation()
    return <PushNotificationSettings onBack={goToPrevious} />
}
